import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Verificar token almacenado al iniciar
  useEffect(() => {
    const token = localStorage.getItem('eas_token');
    if (!token) { setLoading(false); return; }

    // Verificar que el token no haya expirado localmente
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp * 1000 < Date.now()) {
        localStorage.removeItem('eas_token');
        setLoading(false);
        return;
      }
    } catch {
      localStorage.removeItem('eas_token');
      setLoading(false);
      return;
    }

    // Validar con el backend
    fetch(`${API}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setUser(data.user))
      .catch(() => localStorage.removeItem('eas_token'))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error de autenticación');
    localStorage.setItem('eas_token', data.token);
    setUser(data.user);
    return data;
  }, []);

  // Cambia la contraseña del usuario actual. El backend invalida los tokens
  // anteriores y devuelve uno nuevo para seguir en esta sesión.
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const token = localStorage.getItem('eas_token');
    const res = await fetch(`${API}/api/auth/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 401) {
      localStorage.removeItem('eas_token');
      setUser(null);
    }
    if (!res.ok) throw new Error(data.error || 'No se pudo cambiar la contraseña');
    localStorage.setItem('eas_token', data.token);
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('eas_token');
    setUser(null);
  }, []);

  const getToken = useCallback(() => localStorage.getItem('eas_token'), []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, getToken, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
